/**
 * Copyright (c) 2025 Tj L Collins, DaddySavesTheWorld, and Mythbound Adventure Studio
 * All Rights Reserved
 *
 * Voice Session Service - API layer for voice session management
 *
 * @fileoverview Voice session CRUD operations with graceful error handling
 * @version 1.0.0
 * @since v0.0.08
 *
 * PURPOSE:
 * Provides API service layer for voice session lifecycle including join, leave,
 * mode changes, and Discord relay control. Implements graceful failure pattern
 * for development until backend endpoints are ready.
 *
 * KEY RESPONSIBILITIES:
 * - Voice session join/leave operations
 * - Voice mode management
 * - Discord relay enable/disable
 * - Session state retrieval
 * - Graceful error handling with toast notifications
 *
 * DEPENDENCIES:
 * - apiConfig for base URL configuration
 * - voiceToastService for error notifications
 *
 * BACKEND INTEGRATION:
 * - POST /api/voice/sessions/join
 * - POST /api/voice/sessions/leave
 * - PUT /api/voice/sessions/{id}/mode
 * - GET /api/voice/sessions/{id}
 * - POST /api/voice/sessions/{id}/relay/enable
 * - POST /api/voice/sessions/{id}/relay/disable
 *
 * CONTRACT ALIGNMENT:
 * - shared/contracts/voice/v1/voice-classification.yaml (modes, max_participants)
 * - Agent_A_Voice_Bridge.md (API specifications)
 *
 * CHANGE LOG:
 * 2026-01-05 - Agent A - Initial implementation with graceful error handling
 */

import { apiConfig } from '../../config/api';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

/**
 * Voice mode types (matches backend contract voice-classification.yaml)
 */
export type VoiceSessionMode = 'transcribe_all' | 'wake_word' | 'push_to_talk';

/**
 * Participant in a voice session
 */
export interface VoiceParticipant {
  participant_id: string;
  participant_name: string;
  joined_at: string;
  is_connected: boolean;
}

/**
 * Voice session state returned from API
 */
export interface VoiceSession {
  session_id: string;
  campaign_id: string;
  participants: VoiceParticipant[];
  mode: VoiceSessionMode;
  relay_enabled: boolean;
  max_participants: number;
  created_at: string;
  last_activity: string;
}

/**
 * Join voice session request
 */
export interface JoinVoiceSessionRequest {
  campaign_id: string;
  participant_id: string;
  participant_name: string;
}

/**
 * Leave voice session request
 */
export interface LeaveVoiceSessionRequest {
  session_id: string;
  participant_id: string;
}

/**
 * Mode change request
 */
export interface ModeChangeRequest {
  mode: VoiceSessionMode;
  requested_by: string;
}

/**
 * Discord relay enable request
 */
export interface EnableRelayRequest {
  discord_channel_id: string;
  requested_by: string;
}

/**
 * Voice session error codes (per Agent_A_Voice_Bridge.md taxonomy)
 */
export enum VoiceSessionErrorCode {
  // 2xxx: Voice Session Errors
  SESSION_NOT_FOUND = 2001,
  SESSION_FULL = 2002,
  SESSION_INACTIVE = 2003,
  NOT_AUTHORIZED = 2004,
  ALREADY_IN_SESSION = 2005,
  INVALID_MODE = 2006,
  SESSION_CREATION_FAILED = 2007,
  GRACE_PERIOD_EXPIRED = 2008,
  
  // 3xxx: Audio Pipeline Errors
  MICROPHONE_DENIED = 3001,
  NO_MICROPHONE = 3002,
  BROWSER_UNSUPPORTED = 3003,
  AUDIO_INIT_FAILED = 3004,
  AUDIO_PLAYBACK_FAILED = 3005,
  BUFFER_UNDERRUN = 3006,
  BUFFER_OVERRUN = 3007,
  PACKET_LOSS_EXCESSIVE = 3008,
  
  // 4xxx: Discord Relay Errors
  DISCORD_BOT_NOT_CONNECTED = 4001,
  DISCORD_CHANNEL_NOT_FOUND = 4002,
  DISCORD_RELAY_FAILED = 4003,
  DISCORD_RELAY_DISCONNECTED = 4004,
  DISCORD_BOT_NOT_IN_SERVER = 4005,
  
  // 9xxx: Generic Errors
  NETWORK_ERROR = 9001,
  UNKNOWN_ERROR = 9999
}

/**
 * API response wrapper
 */
export interface ApiResponse<T> {
  success: boolean;
  data: T | null;
  error: {
    code: VoiceSessionErrorCode;
    message: string;
  } | null;
}

// ============================================================================
// ERROR HANDLING
// ============================================================================

/**
 * Error message mapping for user-friendly display
 */
const ERROR_MESSAGES: Record<VoiceSessionErrorCode, string> = {
  // 2xxx: Voice Session Errors
  [VoiceSessionErrorCode.SESSION_NOT_FOUND]: 'Voice session not found.',
  [VoiceSessionErrorCode.SESSION_FULL]: 'Session full (6/6 participants). Try again later.',
  [VoiceSessionErrorCode.SESSION_INACTIVE]: 'This voice session has ended.',
  [VoiceSessionErrorCode.NOT_AUTHORIZED]: 'Not authorized for this campaign.',
  [VoiceSessionErrorCode.ALREADY_IN_SESSION]: 'You are already in this voice session.',
  [VoiceSessionErrorCode.INVALID_MODE]: 'Invalid voice mode selected.',
  [VoiceSessionErrorCode.SESSION_CREATION_FAILED]: 'Failed to create voice session.',
  [VoiceSessionErrorCode.GRACE_PERIOD_EXPIRED]: 'Reconnection window expired. Please join again.',
  
  // 3xxx: Audio Pipeline Errors
  [VoiceSessionErrorCode.MICROPHONE_DENIED]: 'Microphone access denied. Check browser permissions.',
  [VoiceSessionErrorCode.NO_MICROPHONE]: 'No microphone detected. Please connect a microphone.',
  [VoiceSessionErrorCode.BROWSER_UNSUPPORTED]: 'Browser does not support voice features. Try Chrome, Firefox, or Edge.',
  [VoiceSessionErrorCode.AUDIO_INIT_FAILED]: 'Audio initialization failed. Try refreshing the page.',
  [VoiceSessionErrorCode.AUDIO_PLAYBACK_FAILED]: 'Audio playback error. Check your speakers/headphones.',
  [VoiceSessionErrorCode.BUFFER_UNDERRUN]: 'Audio buffer underrun. Voice may be choppy - try switching to Smooth buffer preset.',
  [VoiceSessionErrorCode.BUFFER_OVERRUN]: 'Audio buffer overrun. Voice may drop out - try switching to Responsive buffer preset.',
  [VoiceSessionErrorCode.PACKET_LOSS_EXCESSIVE]: 'Excessive packet loss detected. Check your network connection.',
  
  // 4xxx: Discord Relay Errors
  [VoiceSessionErrorCode.DISCORD_BOT_NOT_CONNECTED]: 'Discord bot is not connected. Contact your GM to restart the bot.',
  [VoiceSessionErrorCode.DISCORD_CHANNEL_NOT_FOUND]: 'Discord voice channel not found. Make sure the channel still exists.',
  [VoiceSessionErrorCode.DISCORD_RELAY_FAILED]: 'Failed to connect Discord relay. The bot may not have voice permissions.',
  [VoiceSessionErrorCode.DISCORD_RELAY_DISCONNECTED]: 'Discord relay disconnected unexpectedly. Attempting to reconnect...',
  [VoiceSessionErrorCode.DISCORD_BOT_NOT_IN_SERVER]: 'Bot is not in your Discord server. Use the invite link to add it.',
  
  // 9xxx: Generic Errors
  [VoiceSessionErrorCode.NETWORK_ERROR]: 'Connection error. Please check your network.',
  [VoiceSessionErrorCode.UNKNOWN_ERROR]: 'Something went wrong. Please try again.'
};

/**
 * Get user-friendly error message from error code
 *
 * @param code - Voice session error code
 * @returns Human-readable error message
 */
export function getErrorMessage(code: VoiceSessionErrorCode): string {
  return ERROR_MESSAGES[code] || ERROR_MESSAGES[VoiceSessionErrorCode.UNKNOWN_ERROR];
}

/**
 * Parse API error response and extract error code
 *
 * @param response - Fetch response object
 * @returns Error code and message
 */
async function parseApiError(response: Response): Promise<{ code: VoiceSessionErrorCode; message: string }> {
  try {
    const errorData = await response.json();
    const code = errorData.error_code || VoiceSessionErrorCode.UNKNOWN_ERROR;
    const message = errorData.message || getErrorMessage(code);
    return { code, message };
  } catch {
    // If JSON parsing fails, map HTTP status to error code
    const code = mapHttpStatusToErrorCode(response.status);
    return { code, message: getErrorMessage(code) };
  }
}

/**
 * Map HTTP status code to voice session error code
 *
 * @param status - HTTP status code
 * @returns Corresponding voice session error code
 */
function mapHttpStatusToErrorCode(status: number): VoiceSessionErrorCode {
  switch (status) {
    case 400:
      return VoiceSessionErrorCode.INVALID_MODE;
    case 403:
      return VoiceSessionErrorCode.NOT_AUTHORIZED;
    case 404:
      return VoiceSessionErrorCode.SESSION_NOT_FOUND;
    case 409:
      return VoiceSessionErrorCode.SESSION_FULL;
    case 500:
    case 502:
    case 503:
      return VoiceSessionErrorCode.SESSION_CREATION_FAILED;
    default:
      return VoiceSessionErrorCode.UNKNOWN_ERROR;
  }
}

// ============================================================================
// API SERVICE CLASS
// ============================================================================

/**
 * Voice Session Service
 *
 * Provides API methods for voice session management with graceful error handling.
 * All methods return ApiResponse wrappers that include success state and error info.
 *
 * GRACEFUL FAILURE:
 * Until backend endpoints are implemented, all API calls will fail gracefully
 * with appropriate error messages. The UI remains functional with null data.
 *
 * @example
 * const result = await voiceSessionService.joinSession({
 *   campaign_id: 'campaign-123',
 *   participant_id: 'user-456',
 *   participant_name: 'DragonSlayer'
 * });
 *
 * if (result.success) {
 *   console.log('Joined session:', result.data.session_id);
 * } else {
 *   console.error('Join failed:', result.error.message);
 * }
 */
class VoiceSessionService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = apiConfig.baseUrl;
  }

  /**
   * Join a voice session for a campaign
   *
   * Creates or joins an existing voice session for the specified campaign.
   * If no session exists, a new one is created automatically.
   *
   * @param request - Join request with campaign_id, participant_id, participant_name
   * @returns ApiResponse containing VoiceSession or error
   *
   * PIPELINE EFFECTS:
   * - Creates voice_session record if none exists
   * - Adds participant to voice_participants table
   * - Updates Redis cache
   * - Emits WebSocket event: voice_session_joined
   *
   * @throws Never throws - returns error in ApiResponse
   */
  async joinSession(request: JoinVoiceSessionRequest): Promise<ApiResponse<VoiceSession>> {
    try {
      const response = await fetch(`${this.baseUrl}/voice/sessions/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        const error = await parseApiError(response);
        console.error('[VoiceSessionService] Join failed:', error);
        return {
          success: false,
          data: null,
          error
        };
      }

      const data: VoiceSession = await response.json();
      console.log('[VoiceSessionService] Joined session:', data.session_id);
      return {
        success: true,
        data,
        error: null
      };
    } catch (error) {
      console.error('[VoiceSessionService] Network error on join:', error);
      return {
        success: false,
        data: null,
        error: {
          code: VoiceSessionErrorCode.NETWORK_ERROR,
          message: getErrorMessage(VoiceSessionErrorCode.NETWORK_ERROR)
        }
      };
    }
  }

  /**
   * Leave a voice session
   *
   * Removes the participant from the voice session. If this is the last
   * participant, the session may be marked inactive depending on persistence mode.
   *
   * @param request - Leave request with session_id and participant_id
   * @returns ApiResponse containing leave confirmation or error
   *
   * PIPELINE EFFECTS:
   * - Removes participant from voice_participants table
   * - Updates session participant_count
   * - Updates Redis cache
   * - Emits WebSocket event: voice_session_left
   *
   * @throws Never throws - returns error in ApiResponse
   */
  async leaveSession(request: LeaveVoiceSessionRequest): Promise<ApiResponse<{ session_id: string; left_at: string }>> {
    try {
      const response = await fetch(`${this.baseUrl}/voice/sessions/leave`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        const error = await parseApiError(response);
        console.error('[VoiceSessionService] Leave failed:', error);
        return {
          success: false,
          data: null,
          error
        };
      }

      const data = await response.json();
      console.log('[VoiceSessionService] Left session:', data.session_id);
      return {
        success: true,
        data,
        error: null
      };
    } catch (error) {
      console.error('[VoiceSessionService] Network error on leave:', error);
      return {
        success: false,
        data: null,
        error: {
          code: VoiceSessionErrorCode.NETWORK_ERROR,
          message: getErrorMessage(VoiceSessionErrorCode.NETWORK_ERROR)
        }
      };
    }
  }

  /**
   * Get voice session state
   *
   * Retrieves the current state of a voice session including participants,
   * mode, and relay status. Useful for reconnection scenarios.
   *
   * @param sessionId - The voice session UUID
   * @returns ApiResponse containing VoiceSession or error
   *
   * PIPELINE EFFECTS:
   * - Read-only, no side effects
   * - Tries Redis cache first, falls back to database
   *
   * @throws Never throws - returns error in ApiResponse
   */
  async getSession(sessionId: string): Promise<ApiResponse<VoiceSession>> {
    try {
      const response = await fetch(`${this.baseUrl}/voice/sessions/${sessionId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const error = await parseApiError(response);
        console.error('[VoiceSessionService] Get session failed:', error);
        return {
          success: false,
          data: null,
          error
        };
      }

      const data: VoiceSession = await response.json();
      return {
        success: true,
        data,
        error: null
      };
    } catch (error) {
      console.error('[VoiceSessionService] Network error on get session:', error);
      return {
        success: false,
        data: null,
        error: {
          code: VoiceSessionErrorCode.NETWORK_ERROR,
          message: getErrorMessage(VoiceSessionErrorCode.NETWORK_ERROR)
        }
      };
    }
  }

  /**
   * Update voice mode for a session
   *
   * Changes the voice listening mode for the entire session.
   * Only the GM (campaign creator) can change the mode.
   *
   * @param sessionId - The voice session UUID
   * @param request - Mode change request with new mode and requester ID
   * @returns ApiResponse containing updated session or error
   *
   * PIPELINE EFFECTS:
   * - Updates voice_session_settings.mode
   * - Updates Redis cache
   * - Emits WebSocket event: voice_mode_changed
   * - Frontend VoiceControls updates mode selector
   *
   * @throws Never throws - returns error in ApiResponse
   */
  async updateMode(sessionId: string, request: ModeChangeRequest): Promise<ApiResponse<{ session_id: string; mode: VoiceSessionMode; updated_at: string }>> {
    try {
      const response = await fetch(`${this.baseUrl}/voice/sessions/${sessionId}/mode`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        const error = await parseApiError(response);
        console.error('[VoiceSessionService] Mode update failed:', error);
        return {
          success: false,
          data: null,
          error
        };
      }

      const data = await response.json();
      console.log('[VoiceSessionService] Mode updated to:', data.mode);
      return {
        success: true,
        data,
        error: null
      };
    } catch (error) {
      console.error('[VoiceSessionService] Network error on mode update:', error);
      return {
        success: false,
        data: null,
        error: {
          code: VoiceSessionErrorCode.NETWORK_ERROR,
          message: getErrorMessage(VoiceSessionErrorCode.NETWORK_ERROR)
        }
      };
    }
  }

  /**
   * Enable Discord voice relay
   *
   * Enables the Discord ↔ Web voice relay for the session.
   * Only the GM can enable relay. Requires Discord bot to be in the channel.
   *
   * @param sessionId - The voice session UUID
   * @param request - Relay enable request with Discord channel ID
   * @returns ApiResponse containing relay status or error
   *
   * PIPELINE EFFECTS:
   * - Updates voice_session_settings.relay_enabled = true
   * - Updates voice_session_settings.discord_channel_id
   * - Triggers Discord bot to join voice channel
   * - Emits WebSocket event: relay_status_changed
   *
   * @throws Never throws - returns error in ApiResponse
   */
  async enableRelay(sessionId: string, request: EnableRelayRequest): Promise<ApiResponse<{ relay_enabled: boolean; discord_channel_id: string; relay_status: string }>> {
    try {
      const response = await fetch(`${this.baseUrl}/voice/sessions/${sessionId}/relay/enable`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        const error = await parseApiError(response);
        console.error('[VoiceSessionService] Enable relay failed:', error);
        return {
          success: false,
          data: null,
          error
        };
      }

      const data = await response.json();
      console.log('[VoiceSessionService] Relay enabled for channel:', data.discord_channel_id);
      return {
        success: true,
        data,
        error: null
      };
    } catch (error) {
      console.error('[VoiceSessionService] Network error on enable relay:', error);
      return {
        success: false,
        data: null,
        error: {
          code: VoiceSessionErrorCode.NETWORK_ERROR,
          message: getErrorMessage(VoiceSessionErrorCode.NETWORK_ERROR)
        }
      };
    }
  }

  /**
   * Disable Discord voice relay
   *
   * Disables the Discord ↔ Web voice relay for the session.
   * Only the GM can disable relay.
   *
   * @param sessionId - The voice session UUID
   * @returns ApiResponse containing relay status or error
   *
   * PIPELINE EFFECTS:
   * - Updates voice_session_settings.relay_enabled = false
   * - Triggers Discord bot to leave voice channel
   * - Emits WebSocket event: relay_status_changed
   *
   * @throws Never throws - returns error in ApiResponse
   */
  async disableRelay(sessionId: string): Promise<ApiResponse<{ relay_enabled: boolean }>> {
    try {
      const response = await fetch(`${this.baseUrl}/voice/sessions/${sessionId}/relay/disable`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const error = await parseApiError(response);
        console.error('[VoiceSessionService] Disable relay failed:', error);
        return {
          success: false,
          data: null,
          error
        };
      }

      const data = await response.json();
      console.log('[VoiceSessionService] Relay disabled');
      return {
        success: true,
        data,
        error: null
      };
    } catch (error) {
      console.error('[VoiceSessionService] Network error on disable relay:', error);
      return {
        success: false,
        data: null,
        error: {
          code: VoiceSessionErrorCode.NETWORK_ERROR,
          message: getErrorMessage(VoiceSessionErrorCode.NETWORK_ERROR)
        }
      };
    }
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

/**
 * Singleton instance of VoiceSessionService
 * Use this for all voice session API operations
 */
export const voiceSessionService = new VoiceSessionService();

export default voiceSessionService;

